import React, { useState, useEffect } from 'react';
import axios from 'axios';
import EditOutwardModal from './EditOutwardModal';
import EntryHistoryTable from '../common/EntryHistoryTable';
import { API_BASE_URL } from '../../config';
import './OutwardEntry.css';

const OutwardEntryHistory = ({ showCreatedBy = false }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/outward-entries`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      // Ensure we have an array of entries, even if empty
      let entriesData = Array.isArray(response.data) ? response.data :
                         response.data.entries ? response.data.entries : [];
      // Deduplicate by id (or serial_number if id is missing)
      const seen = new Set();
      entriesData = entriesData.filter(entry => {
        const key = entry.id || entry.serial_number;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setEntries(entriesData);
    } catch (err) {
      setError('Error fetching entries');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    // Always set out_date to today when editing
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const currentDate = `${yyyy}-${mm}-${dd}`;

    setEditingEntry({
      ...entry,
      materials_list: entry.materials_list || [],
      out_date: currentDate // Always force today's date
    });
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setEditingEntry(null);
    setError('');
  };

  const handleMaterialChange = (index, field, value) => {
    setEditingEntry(prev => {
      const updatedMaterials = [...(prev.materials_list || [])];
      updatedMaterials[index] = {
        ...updatedMaterials[index],
        [field]: value
      };
      return {
        ...prev,
        materials_list: updatedMaterials
      };
    });
  };

  const addMaterial = () => {
    setEditingEntry(prev => ({
      ...prev,
      materials_list: [
        ...(prev.materials_list || []),
        { name: '', quantity: '', uom: '' }
      ]
    }));
  };

  const removeMaterial = (index) => {
    setEditingEntry(prev => ({
      ...prev,
      materials_list: (prev.materials_list || []).filter((_, i) => i !== index)
    }));
  };

  const handleChange = (field, value) => {
    setEditingEntry(prev => {
      let updated = { ...prev, [field]: value };
      // Tare weight validation: must be >= 0
      if (field === 'tare_weight') {
        const tare = parseFloat(value);
        if (tare < 0) {
          setError('Tare weight cannot be negative.');
          return prev;
        }
      }
      // Auto-calculate net_weight if gross_weight or tare_weight changes
      if (field === 'gross_weight' || field === 'tare_weight') {
        const gross = parseFloat(field === 'gross_weight' ? value : prev.gross_weight) || 0;
        const tare = parseFloat(field === 'tare_weight' ? value : prev.tare_weight) || 0;
        let net = gross - tare;
        if (net < 0) net = 0;
        updated.net_weight = net;
      }
      return updated;
    });
  };

  const saveChanges = async () => {
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');

      // Get current time in HH:mm format
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      const currentTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;

      const updateData = {
        purpose: editingEntry.purpose,
        check_by: editingEntry.check_by,
        party_name: editingEntry.party_name,
        remarks: editingEntry.remarks,
        time_out: currentTime,
        materials: editingEntry.materials_list.map(m => ({
          name: m.name || '',
          quantity: m.quantity || '',
          uom: m.uom || ''
        })),
        gross_weight: editingEntry.gross_weight,
        tare_weight: editingEntry.tare_weight,
        weight_slip_number: editingEntry.weight_slip_number,
        out_date: editingEntry.out_date,
        bills: editingEntry.bills || [] // <-- send bills array for saving
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/outward-entries/${editingEntry.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` }}
      );

      if (response.data) {
        setSuccess('Changes saved successfully!');
        setTimeout(() => {
          handleEditSave();
        }, 300); // Close modal after short delay
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message); // <-- Show backend error message
      } else {
        setError('Error saving changes');
      }
    }
  };

  // Refetch entries after editing to avoid duplicates and ensure fresh data
  const handleEditSave = async () => {
    // Only update state after async fetch, not during render
    await fetchEntries();
    setEditingEntry(null);
  };

  // Prevent state updates during render by not calling setEditingEntry in render
  // Only call setEditingEntry in event handlers like handleEdit

  if (loading) {
    return <div className="loading">Loading entries...</div>;
  }

  return (
    <div className="outward-entry-history">
      <h2>Outward Entry History</h2>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      {entries.length === 0 ? (
        <div className="no-entries">No entries found</div>
      ) : (
        <EntryHistoryTable 
          entries={entries}
          onEdit={handleEdit}
          type="outward"
          showCreatedBy={showCreatedBy}
          // Ensure bills are displayed as a table for each entry
        />
      )}

      {editingEntry && (
        <EditOutwardModal
          editingEntry={editingEntry}
          handleChange={handleChange}
          handleMaterialChange={handleMaterialChange}
          addMaterial={addMaterial}
          removeMaterial={removeMaterial}
          handleSave={saveChanges}
          handleCancel={handleCancel}
          error={error}
          success={success}
          // Do NOT pass any prop that would allow editing out_date
        />
      )}
    </div>
  );
};

export default OutwardEntryHistory;
